<template>
  <div class="animated fadeIn">
    <b-row>
      <b-col>
        <hm-spinner v-if="!ready"/>
        <b-form @submit.prevent="onSubmit">
          <b-card header="Designated Privacy Officer (PO)" v-show="ready">
            <Loading :active="busy" :is-full-page="false">
              <slot>
                <hm-spinner/>
              </slot>
            </Loading>
            <hm-alert/>
            <div>
              <p>
                Designate a person who’ll act as both the Privacy Officer and Security Officer (hereinafter privacy officer or simply PO) for the practice. Select a person who is considered senior-level
                <hm-popover title="Senior Level">
                  For a typical small practice &quot;senior-level&quot; is an office manager, provider/owner, or someone else who's been with the practice for an extended period of time and has a good understanding of daily operations. In contrast, you wouldn't want to
                  choose a new hire for the role of PO.
                </hm-popover>within the organization and who is qualified to fulfill the job responsibilities below.
              </p>
              <hm-note>
                <u>Note</u>: It is not expected, nor reasonable, for a small practice to hire professionally educated IT security personnel to be their PO. HIPAAMATE will make a reasonable effort to provide useful guidance on these topics
                to aid the PO.
              </hm-note>
              <h4>Privacy Officer Job Responsibilities</h4>
              <div>
                <ul class="ul-check">
                  <li>Be the primary contact for all security matters</li>
                  <li>Periodically completing a risk analysis</li>
                  <li>Advise on current system capabilities, vulnerabilities, and leading practices for mitigation</li>
                  <li>Implement policies and procedures for security</li>
                  <li>Communicate and educate workforce about security policies and procedures</li>
                  <li>Assure policies, procedures and other security program documentation are available to those who need it to perform the responsibilities associated with their role</li>
                  <li>Help management decide on security purchases (products and services)</li>
                  <li>Assure the security of information system security</li>
                  <li>Knowledge of the automatic logoff settings for its information systems and electronic devices</li>
                  <li>Verify settings for hardware and software are activated</li>
                  <li>Review records of information system activity, such as audit logs, access reports, and security incident tracking reports on a regular basis</li>
                  <li>Participate in workforce security</li>
                  <li>Support vendor management</li>
                  <li>Supervise information system maintenance activities (whether completed by members of your workforce or vendors)</li>
                  <li>Support facility security planning</li>
                  <li>Support continuity planning</li>
                  <li>Support plans for emergency mode of operations (including access to ePHI)</li>
                  <li>Support information and information system recovery and resumption of routine practice operation after an emergency</li>
                  <li>Make sure business associate agreements are in place before your practice enables a service provider (business associate) to begin to create, access, store or transmit ePHI on your practice’s behalf.</li>
                  <li>Decide who can access ePHI (and under what conditions), potentially with input from more senior leadership/owner(s)/provider(s), and create ePHI access rules that others can follow.</li>
                  <li>Engage in ongoing evaluation, monitoring, and reporting.</li>
                </ul>
              </div>
            </div>
            <div>
              <p>
                <strong>Who is the designated Privacy Officer for the practice?</strong>
              </p>
            </div>

            <b-form-group label="Full Name">
              <b-form-input
                name="fullName"
                type="text"
                class="form-control"
                placeholder="Full Name"
                autocomplete="name"
                v-model="practice.po.name"
                v-validate="'required'"
                :disabled="busy"
                :class="{ 'is-invalid': errors.has('fullName') && submitted }"
              ></b-form-input>
            </b-form-group>
            <b-form-group label="Email">
              <b-form-input
                name="email"
                type="text"
                class="form-control"
                placeholder="Email"
                autocomplete="email"
                v-model="practice.po.email"
                v-validate="'required|email'"
                :disabled="busy"
                :class="{ 'is-invalid': errors.has('email') && submitted }"
              ></b-form-input>
            </b-form-group>
            <b-form-group label="Phone">
              <the-mask
                name="phone"
                :mask="'(###) ###-####'"
                type="text"
                class="form-control"
                placeholder="(xxx) xxx-xxxx"
                autocomplete="phone"
                v-model="practice.po.phone"
                v-validate="'required|numeric|length:10'"
                :disabled="busy"
                :class="{ 'is-invalid': errors.has('phone') && submitted }"
              ></the-mask>
            </b-form-group>
            <div slot="footer">
              <b-row>
                <b-col class="text-right">
                  <b-button type="submit" variant="primary" class="px-4" :disabled="busy">Save</b-button>
                </b-col>
              </b-row>
            </div>
          </b-card>
        </b-form>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { practiceService } from '@/services';

export default {
  name: 'Practice.PrivacyOfficer',
  data() {
    return {
      practice: {
        po: {
          name: '',
          email: '',
          phone: ''
        }
      },
      ready: false,
      busy: false,
      submitted: false
    }
  },
  methods: {
    ...mapActions({
      alertClear: 'alert/clear',
      alertSuccess: 'alert/success',
      alertError: 'alert/error',
      alertException: 'alert/exception',
      setProgress: 'progress/setFlagDone'
    }),
    onSubmit() {
      this.busy = true;
      this.submitted = true;
      this.alertClear();

      this.$validator.validate()
        .then(valid => {
          if (valid) {
            return practiceService.setPractice(this.practice)
              .then(data => {
                this.setProgress('POSet');
                this.alertSuccess('Privacy officer changes saved successfully!');
              })
              .catch(err => {
                this.alertException({ err, message: 'Error saving privacy officer.' });
              });
          } else {
            this.alertError('Please complete privacy officer information and submit again.');
          }
        })
        .finally(() => {
          this.busy = false;
        });
    }
  },
  created() {
    this.ready = false;

    practiceService.getPractice()
      .then(data => {
        // set any defaults that are missing from data to the default values
        this.practice = { ...this.$options.data.apply(this).practice, ...data };
      })
      .catch(err => {
        if (err.status !== 404) { // 404, practice may not be setup yet
          this.alertException({ err, message: 'Error loading practice.' });
        }
      })
      .finally(() => {
        this.ready = true;
      });
  }
}
</script>

<style>
/* icon unordered list */
.ul-check {
  list-style-type: none;
}
.ul-check li:before {
  font-family: 'simple-line-icons';
  content: '\e080';
  margin: 0 0.5em 0 -1.2em;
  color: #4dbd74;
}
</style>